import axios from "axios";
import { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Alert,
  Badge,
  Button,
  Card, CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner
} from "reactstrap";
import * as XLSX from "xlsx";
import api from "../../services/api";


const BulkPaymentUpload = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedTechnology, setSelectedTechnology] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("");
  const [courses, setCourses] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [technologies, setTechnologies] = useState([]);
  const [technologiesLoading, setTechnologiesLoading] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(false);

  // Fetch courses on component mount
  useEffect(() => {
    fetchCourses();
  }, []);

  // Fetch programs when course changes
  useEffect(() => {
    if (selectedCourse) {
      fetchPrograms(selectedCourse);
    } else {
      setPrograms([]);
      setSelectedProgram("");
      setTechnologies([]);
      setSelectedTechnology("");
    }
  }, [selectedCourse]);

  // Fetch technologies when program changes
  useEffect(() => {
    if (selectedProgram) {
      fetchTechnologies(selectedProgram);
    } else {
      setTechnologies([]);
      setSelectedTechnology("");
    }
  }, [selectedProgram]);

  const fetchCourses = async () => {
    setCoursesLoading(true);
    try {
      const response = await axios.get(`${backendUrl}get-courses`);
      if (response.data && Array.isArray(response.data)) {
        setCourses(response.data);
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
      toast.error("Failed to load courses");
    } finally {
      setCoursesLoading(false);
    }
  };

  const fetchPrograms = async (courseId) => {
    setProgramsLoading(true);
    try {
      const response = await axios.get(`${backendUrl}get-programs-by-course-id/${courseId}`);

      if (response.data && response.data.coursePrograms && Array.isArray(response.data.coursePrograms)) {
        const programData = response.data.coursePrograms.map(program => ({
          program_name: program.program_name
        }));
        setPrograms(programData);

        // Auto-select if only one program
        if (programData.length === 1) {
          setSelectedProgram(programData[0].program_name);
        } else {
          setSelectedProgram("");
        }
      } else {
        setPrograms([]);
        setSelectedProgram("");
      }
    } catch (error) {
      console.error("Error fetching programs:", error);
      toast.error("Failed to load programs");
      setPrograms([]);
      setSelectedProgram("");
    } finally {
      setProgramsLoading(false);
    }
  };

  const fetchTechnologies = async (programName) => {
    setTechnologiesLoading(true);
    try {
      const response = await axios.get(`${backendUrl}get-technology-by-program/${programName}`);

      if (response.data && response.data.Technology && Array.isArray(response.data.Technology)) {
        const techData = response.data.Technology.map(tech => ({
          _id: tech._id, // This will be used for reg_id
          technology_id: tech.technology_id, // This will be used for technology parameter
          name: tech.technology_name,
          icon: tech.technology_icon
        }));
        setTechnologies(techData);

        // Auto-select if only one technology
        if (techData.length === 1) {
          setSelectedTechnology(techData[0].technology_id);
        } else {
          setSelectedTechnology("");
        }
      } else {
        setTechnologies([]);
        setSelectedTechnology("");
      }
    } catch (error) {
      console.error("Error fetching technologies:", error);
      toast.error("Failed to load technologies");
      setTechnologies([]);
      setSelectedTechnology("");
    } finally {
      setTechnologiesLoading(false);
    }
  };

  // Download Excel template
  const downloadTemplate = () => {
    const templateData = [
      {
        roll_no: "5896",
        payment_eligible: 1
      },
      {
        roll_no: "5897",
        payment_eligible: 1
      },
      {
        roll_no: "5898",
        payment_eligible: 0
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");

    const maxRollNoLength = Math.max(...templateData.map(row => String(row.roll_no).length));
    worksheet['!cols'] = [
      { wch: maxRollNoLength + 2 },
      { wch: 15 }
    ];

    XLSX.writeFile(workbook, "bulk_payment_template.xlsx");
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/octet-stream'
      ];

      if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        setErrorMsg("Please select a valid Excel file (.xlsx or .xls)");
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setErrorMsg("");
      setUploadResult(null);
      setShowResults(false);
    }
  };

  // Handle bulk upload
  const handleBulkUpload = async () => {
    if (!selectedCourse) {
      setErrorMsg("Please select a course");
      return;
    }

    if (!selectedProgram) {
      setErrorMsg("Please select a program");
      return;
    }

    if (!selectedTechnology) {
      setErrorMsg("Please select a technology");
      return;
    }

    if (!selectedFile) {
      setErrorMsg("Please select an Excel file");
      return;
    }

    setUploadLoading(true);
    setErrorMsg("");
    setUploadResult(null);
    setShowResults(false);

    // Find the selected technology object to get both _id and technology_id
    const selectedTechObj = technologies.find(tech => tech.technology_id === selectedTechnology);

    if (!selectedTechObj) {
      setErrorMsg("Selected technology not found");
      setUploadLoading(false);
      return;
    }

    console.log("Upload Data:", {
      courseId: selectedCourse,
      program: selectedProgram,
      technology: selectedTechObj.technology_id, // technology_id for technology parameter
      reg_id: selectedTechObj._id, // _id for reg_id parameter
      file: selectedFile.name
    });

    console.log("Course ID:", selectedCourse);
    console.log("Program:", selectedProgram);
    console.log("Technology ID (technology parameter):", selectedTechObj.technology_id);
    console.log("Technology _id (reg_id parameter):", selectedTechObj._id);
    console.log("File:", selectedFile.name);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("courseId", selectedCourse); // course _id
      formData.append("technology", selectedTechObj.technology_id); // technology_id for technology parameter
      formData.append("reg_id", selectedTechObj._id); // _id for reg_id parameter

      // Log FormData contents for debugging
      console.log("FormData contents:");
      for (let [key, value] of formData.entries()) {
        console.log(key, value);
      }

      const response = await api.post(`${backendUrl}upload-payment-status`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 30000, // 30 seconds timeout
      });

      console.log("Upload response:", response);

      if (response.status === 200) {
        console.log(response.data);
        setUploadResult(response.data);
        setShowResults(true);
        toast.success("Bulk upload completed successfully!");
        setSelectedFile(null);
        document.getElementById("fileInput").value = "";
      }
    } catch (error) {
      console.error("Upload error details:", error);

      // Enhanced error logging
      if (error.response) {
        // Server responded with error status
        console.error("Server response error:", error.response.status, error.response.data);
        const errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
        setErrorMsg(errorMessage);
        toast.error(errorMessage);
      } else if (error.request) {
        // Request made but no response received
        console.error("No response received:", error.request);
        setErrorMsg("No response from server. Please check your connection.");
        toast.error("No response from server. Please check your connection.");
      } else {
        // Something else happened
        console.error("Error:", error.message);
        setErrorMsg(error.message || "Upload failed. Please try again.");
        toast.error(error.message || "Upload failed. Please try again.");
      }
    } finally {
      setUploadLoading(false);
    }
  };

  // Result card component for different status types
  const ResultCard = ({ title, count, items, color, icon }) => (
    <Card className="h-100 border-0 shadow-sm">
      <CardBody className="p-4">
        <div className="d-flex align-items-center mb-3">
          <div className={`bg-${color} bg-opacity-10 rounded-circle p-3 me-3`}>
            <i className={`fas ${icon} text-${color} fs-4`}></i>
          </div>
          <div>
            <h4 className={`text-${color} mb-0`}>{count}</h4>
            <span className="text-muted">{title}</span>
          </div>
        </div>

        {items && items.length > 0 ? (
          <div className="mt-3">
            <div className="d-flex flex-wrap gap-2">
              {items.map((item, index) => (
                <Badge
                  key={index}
                  color={color}
                  className="fs-6 py-2 px-3"
                >
                  {typeof item === "object"
                    ? `${item.roll_no} (${item.technology_name})`
                    : item}
                </Badge>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-muted mt-2">
            <small>No records found</small>
          </div>
        )}
      </CardBody>
    </Card>
  );


  return (
    <div className="container-fluid mt-4">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Page Header */}
      <Row className="mb-4">
        <Col>
          <h2 className="fw-bold text-primary">Bulk Payment Status Update</h2>
          <p className="text-muted">
            Upload Excel file to update payment status for multiple students
          </p>
        </Col>
      </Row>

      {/* Main Upload Card */}
      <Row className="justify-content-center">
        <Col lg={8} md={10}>
          <Card className="shadow-sm border-0" style={{ borderRadius: "12px" }}>
            <CardHeader className="bg-primary text-white border-0">
              <h5 className="fw-bold mb-0">Upload Payment Data</h5>
            </CardHeader>
            <CardBody className="p-4">

              {/* Course Selection */}
              <FormGroup>
                <Label for="courseSelect" className="fw-semibold">
                  Select Course <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="courseSelect"
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                  disabled={coursesLoading}
                  bsSize="lg"
                >
                  <option value="">Choose a course...</option>
                  {courses.map((course) => (
                    <option key={course._id} value={course._id}>
                      {course.course_name}
                    </option>
                  ))}
                </Input>
                {coursesLoading && (
                  <div className="mt-2">
                    <Spinner size="sm" className="me-2" />
                    Loading courses...
                  </div>
                )}
              </FormGroup>

              {/* Program Selection */}
              <FormGroup>
                <Label for="programSelect" className="fw-semibold">
                  Select Program <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="programSelect"
                  value={selectedProgram}
                  onChange={(e) => setSelectedProgram(e.target.value)}
                  disabled={!selectedCourse || programsLoading}
                  bsSize="lg"
                >
                  <option value="">Choose a program...</option>
                  {programs.map((program, index) => (
                    <option key={index} value={program.program_name}>
                      {program.program_name}
                    </option>
                  ))}
                </Input>
                {programsLoading && (
                  <div className="mt-2">
                    <Spinner size="sm" className="me-2" />
                    Loading programs...
                  </div>
                )}
                {!selectedCourse && (
                  <div className="text-muted mt-1">
                    <small>Please select a course first</small>
                  </div>
                )}
                {selectedCourse && programs.length === 0 && !programsLoading && (
                  <div className="text-warning mt-1">
                    <small>No programs found for this course</small>
                  </div>
                )}
              </FormGroup>

              {/* Technology Selection */}
              <FormGroup>
                <Label for="technologySelect" className="fw-semibold">
                  Select Technology <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="technologySelect"
                  value={selectedTechnology}
                  onChange={(e) => setSelectedTechnology(e.target.value)}
                  disabled={!selectedProgram || technologiesLoading}
                  bsSize="lg"
                >
                  <option value="">Choose a technology...</option>
                  {technologies.map((tech) => (
                    <option key={tech.technology_id} value={tech.technology_id}>
                      {tech.name}
                    </option>
                  ))}
                </Input>
                {technologiesLoading && (
                  <div className="mt-2">
                    <Spinner size="sm" className="me-2" />
                    Loading technologies...
                  </div>
                )}
                {!selectedProgram && (
                  <div className="text-muted mt-1">
                    <small>Please select a program first</small>
                  </div>
                )}
                {selectedProgram && technologies.length === 0 && !technologiesLoading && (
                  <div className="text-warning mt-1">
                    <small>No technologies found for this program</small>
                  </div>
                )}
              </FormGroup>

              {/* File Upload Section */}
              <FormGroup>
                <Label for="fileInput" className="fw-semibold">
                  Upload Excel File <span className="text-danger">*</span>
                </Label>
                <Input
                  type="file"
                  id="fileInput"
                  accept=".xlsx, .xls"
                  onChange={handleFileSelect}
                  bsSize="lg"
                />
                <div className="text-muted mt-1">
                  <small>
                    Accepted formats: .xlsx, .xls | Max file size: 10MB
                  </small>
                </div>
              </FormGroup>

              {/* Selected File Info */}
              {selectedFile && (
                <Alert color="info" className="mt-3">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-file-excel text-success me-2 fs-5"></i>
                    <div>
                      <strong>Selected file:</strong> {selectedFile.name}
                      <br />
                      <strong>Size:</strong> {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </Alert>
              )}

              {/* Error Message */}
              {errorMsg && (
                <Alert color="danger" className="mt-3">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {errorMsg}
                  </div>
                </Alert>
              )}

              {/* Action Buttons */}
              <Row className="mt-4 g-3">
                <Col md={6}>
                  <Button
                    color="outline-primary"
                    className="w-100 fw-semibold"
                    onClick={downloadTemplate}
                    size="lg"
                  >
                    <i className="fas fa-download me-2"></i>
                    Download Template
                  </Button>
                </Col>
                <Col md={6}>
                  <Button
                    color="primary"
                    className="w-100 fw-semibold"
                    onClick={handleBulkUpload}
                    disabled={uploadLoading || !selectedFile || !selectedCourse || !selectedProgram || !selectedTechnology}
                    size="lg"
                  >
                    {uploadLoading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-upload me-2"></i>
                        Upload
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Results Section */}
      {showResults && uploadResult && (
        <Row className="justify-content-center mt-4">
          <Col lg={12} md={12}>
            <Card className="shadow-sm border-0" style={{ borderRadius: "12px" }}>
              <CardHeader className="bg-success text-white border-0">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="fw-bold mb-0">
                    <i className="fas fa-check-circle me-2"></i>
                    Upload Results
                  </h5>
                  <Badge color="light" className="text-success fs-6">
                    Total in Excel: {uploadResult.summary?.totalInExcel || 0}
                  </Badge>
                </div>
              </CardHeader>
              <CardBody className="p-4">

                {/* Summary Alert */}
                <Alert color="success" className="mb-4">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-info-circle me-2 fs-5"></i>
                    <div>
                      <strong>{uploadResult.message}</strong>
                    </div>
                  </div>
                </Alert>

                {/* Results Grid */}
                <Row className="g-4">
                  <Col lg={3} md={6}>
                    <ResultCard
                      title="Successfully Updated"
                      count={uploadResult.summary?.updated?.length || 0}
                      items={uploadResult.summary?.updated}
                      color="success"
                      icon="fa-check-circle"
                    />
                  </Col>
                  <Col lg={3} md={6}>
                    <ResultCard
                      title="Already Payments Enabled"
                      count={uploadResult.summary?.alreadyEnabled?.length || 0}
                      items={uploadResult.summary?.alreadyEnabled}
                      color="primary"
                      icon="fa-check-circle"
                    />
                  </Col>

                  <Col lg={3} md={6}>
                    <ResultCard
                      title="Payment in Same Tech"
                      count={uploadResult.summary?.hasPaymentInSameTechnology?.length || 0}
                      items={uploadResult.summary?.hasPaymentInSameTechnology}
                      color="info"
                      icon="fa-sync-alt"
                    />
                  </Col>

                  <Col lg={3} md={6}>
                    <ResultCard
                      title="Payment in Other Tech"
                      count={uploadResult.summary?.hasPaymentInOtherTechnology?.length || 0}
                      items={uploadResult.summary?.hasPaymentInOtherTechnology}
                      color="warning"
                      icon="fa-exchange-alt"
                    />
                  </Col>

                  <Col lg={3} md={6}>
                    <ResultCard
                      title="Not Registered"
                      count={uploadResult.summary?.notFound?.length || 0}
                      items={uploadResult.summary?.notFound}
                      color="danger"
                      icon="fa-exclamation-triangle"
                    />
                  </Col>
                </Row>

                <div className="text-center mt-4">
                  <Button
                    color="outline-secondary"
                    onClick={() => setShowResults(false)}
                    size="lg"
                  >
                    <i className="fas fa-times me-2"></i>
                    Close Results
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default BulkPaymentUpload;
